import React from 'react';
import parse from 'html-react-parser';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types';
import HubspotForm from 'react-hubspot-form';
// Components
import ContentModelHandler from '../../utils/ContentModelHandler';
import Lottie from '../lottie';
import PerksForm from '../form/PerksForm';

// TODO: Better TS definitions
const RichTextRenderer = ({
  text,
  fontSize,
  desktopFontSize,
  textStyle,
  extraClasses,
  richTextClasses,
}: any) => {
  const options = {
    renderMark: {
      [MARKS.CODE]: (text: any) => {
        return parse(text);
      },
    },
    renderNode: {
      [INLINES.EMBEDDED_ENTRY]: (node: any) => {
        const inlineEmbeddedEntry = text.references.filter(
          (entry: any) => entry.contentful_id === node.data.target.sys.id,
        );

        return ContentModelHandler(inlineEmbeddedEntry);
      },

      [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
        const embeddedEntry = text.references.filter(
          (entry: any) => entry.contentful_id === node.data.target.sys.id,
        );

        return ContentModelHandler(embeddedEntry);
      },

      [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
        // eslint-disable-next-line react/prop-types
        const file = text.references.filter(
          (file: any) => file.contentful_id === node.data.target.sys.id,
        );

        const asset = file[0];

        if (asset) {
          if (asset.file.contentType && asset.file.contentType.includes('video')) {
            return (
              <iframe
                className='cl-video'
                src={asset.file.url}
                allow='accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture'
                frameBorder={0}
                allowFullScreen
              />
            );
          }

          if (asset.mimeType === 'application/json') {
            return <Lottie animationData={asset.url} />;
          }
          return <img src={asset.file.url} alt={asset.description} loading='lazy' />;
        }
      },
      [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
        const includesCode = node.content[0].marks[0]
          ? node.content[0].marks[0].type === 'code'
            ? true
            : false
          : false;

        // removed empty array items
        const filteredChildren = children.filter((child: any) => child !== '');

        return (
          <React.Fragment>
            {includesCode
              ? filteredChildren.map((child: any) => {
                  if (includesCode) {
                    return child;
                  }
                })
              : filteredChildren.length !== 0 && (
                  <p>
                    {filteredChildren.map((child: any, index: number) => {
                      if (child.type == 'b' || child !== '') {
                        return child;
                      } else if (child.type == 'a') {
                        return (
                          <a href={child.props.href} rel='noreferrer' key={index}>
                            {child.props.children.map(
                              (linkableElements: string) => linkableElements,
                            )}
                          </a>
                        );
                      }
                    })}
                  </p>
                )}
          </React.Fragment>
        );
      },
      [BLOCKS.UL_LIST]: (node: { content: Array<object> }) => {
        return (
          <ul className='cl-list'>
            {node.content.map((list: any, index: number) => {
              return (
                <li key={index}>
                  {list.content[0].content.map((content: any) => {
                    if (content.nodeType === 'text') {
                      if (content.marks.some((mark: any) => mark.type === 'bold')) {
                        return <b key={content.value}>{content.value}</b>;
                      } else if (content.marks.some((mark: any) => mark.type === 'code')) {
                        return parse(content.value);
                      } else {
                        return content.value;
                      }
                    } else if (content.nodeType === 'hyperlink') {
                      return (
                        <a
                          href={content.data.uri}
                          rel='noreferrer'
                          key={index}
                          className='text-secondary-500 hover:text-secondary-600 focus:text-secondary-700'
                        >
                          {content.content[0].value}
                        </a>
                      );
                    } else {
                      // There is an embedded entry on the list to render
                      const embeddedEntry = text.references.find(
                        (entry: any) => entry.contentful_id === content.data.target.sys.id,
                      );
                      return ContentModelHandler([embeddedEntry]);
                    }
                  })}
                </li>
              );
            })}
          </ul>
        );
      },
      [BLOCKS.OL_LIST]: (node: { content: Array<object> }) => {
        return (
          <ol className='cl-list'>
            {node.content.map((list: any, index: number) => {
              return <li key={index}>{list.content[0].content[0].value}</li>;
            })}
          </ol>
        );
      },
      [BLOCKS.QUOTE]: (node: any) => {
        return (
          <figure>
            <blockquote>
              {node.content.map((quote: any, index: number) => {
                switch (index) {
                  case 0:
                    return <p key={index}>{quote.content[0].value}</p>;
                  case 1:
                    return <figcaption key={index}>{quote.content[0].value}</figcaption>;
                  case 2:
                    return <cite key={index}>{quote.content[0].value}</cite>;
                  default:
                    return <p key={index}>{quote.content[0].value}</p>;
                }
              })}
            </blockquote>
          </figure>
        );
      },
      [BLOCKS.TABLE]: (node: any, children: any) => (
        <div className='cl-table-container'>
          <table>
            <tbody>{children}</tbody>
          </table>
        </div>
      ),
    },
  };

  return (
    <div
      className={`cl-text-block ${fontSize ? `cl-text-block--${fontSize}` : ''}${
        desktopFontSize ? `lg:cl-text-block--${desktopFontSize}` : ''
      } ${richTextClasses && richTextClasses} ${extraClasses ? extraClasses : ''} ${
        textStyle === 'Prose' ? 'cl-prose' : ''
      }`}
    >
      {text && documentToReactComponents(JSON.parse(text.raw), options)}
      {extraClasses && extraClasses.includes('cl-hubspot-form') && (
        <HubspotForm
          region='na1'
          portalId='3942778'
          formId='431d0f9d-33f7-4c8d-b2e9-78a5c5895dc0'
          onSubmit={() => console.log('Submit!')}
          loading={
            <div className='flex justify-center items-center'>
              <div className='inline-flex gap-2 justify-center items-center px-6 h-12 font-medium tracking-wide text-primary-500'>
                <span className='relative only:-mx-6'>
                  <svg
                    className='w-6 h-6 animate-spin text-primary-500'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    ></circle>
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    ></path>
                  </svg>
                </span>
                <span>Loading form...</span>
              </div>
            </div>
          }
          css=''
          cssClass='cl-hubspot-form'
          submitButtonClass='cl-button cl-button--primary cl-button--elevated'
          redirectUrl='/partners-signup-thankyou'
        />
      )}
      {extraClasses && extraClasses.includes('cl-hubspot-perks-form') && (
        <HubspotForm
          region='na1'
          portalId='3942778'
          formId='48e151ff-4566-413f-a531-44d5ed3689fe'
          onSubmit={() => console.log('Submit!')}
          loading={
            <div className='flex justify-center items-center'>
              <div className='inline-flex gap-2 justify-center items-center px-6 h-12 font-medium tracking-wide text-primary-500'>
                <span className='relative only:-mx-6'>
                  <svg
                    className='w-6 h-6 animate-spin text-primary-500'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    ></circle>
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    ></path>
                  </svg>
                </span>
                <span>Loading form...</span>
              </div>
            </div>
          }
          css=''
          cssClass='cl-hubspot-perks-form'
          submitButtonClass='cl-button cl-button--primary cl-button--elevated'
        />
      )}
      {extraClasses && extraClasses.includes('cl-hubspot-callback-form') && (
        <HubspotForm
          region='na1'
          portalId='3942778'
          formId='4c12dabb-1342-488d-9f4a-7788c61a46d5'
          onSubmit={() => console.log('Submit!')}
          loading={
            <div className='flex justify-center items-center'>
              <div className='inline-flex gap-2 justify-center items-center px-6 h-12 font-medium tracking-wide text-primary-500'>
                <span className='relative only:-mx-6'>
                  <svg
                    className='w-6 h-6 animate-spin text-primary-500'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    ></circle>
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    ></path>
                  </svg>
                </span>
                <span>Loading form...</span>
              </div>
            </div>
          }
          css=''
          cssClass='cl-hubspot-perks-form cl-callback-form'
          submitButtonClass='cl-button cl-button--primary cl-button--elevated'
        />
      )}
    </div>
  );
};

export default RichTextRenderer;
